import { useState } from 'react'
import { Flex, Spinner } from '@radix-ui/themes'
import { Link } from '@tanstack/react-router'
import { IconSearch } from '@tabler/icons-react'
import { useGetChapterByClass } from '@/services/chapterService'
import { Result, Status } from '@/types/status'
import { useModulesStore } from '@/stores/useModulesStore'
import { cn } from '@/lib/utils'
import { getMarksPercentage, getMarksStyle } from '@/utils/style'
import { useDebounce } from '@/hooks/useDebounce'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import TablePaginate from '@/components/ui/table-paginate'

export interface MarkTableItem {
  id: string
  name: string
  module: string
  time: string
  attempts?: number
  result: Result
  aiMarks: number
  tutorMarks?: number
  status: Status
}

const ChapterTable = ({ classId }: { classId: string }) => {
  const { setSelectedChapter } = useModulesStore()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')

  const debouncedQuery = useDebounce(search)

  const { classChapters, isLoadingClassChapter, meta } = useGetChapterByClass({
    module_id: classId.toString(),
    page,
    limit: 10,
    search: debouncedQuery.trim(),
  })

  const handlePageChange = (page: number) => {
    setPage(page)
  }

  return (
    <Card className='z-100 py-1'>
      <CardContent className='p-0'>
        <div className={'space-y-6 rounded-lg bg-[#FFFFFF] p-4'}>
          <Flex align={'center'} gap={'3'} justify={'between'}>
            <Flex align={'center'} gap={'3'}>
              <p className='text-2xl text-[#002652]'>
                Questions Listed by Chapter
              </p>
              <Link
                to='/question-lib/$quesiontionLibId'
                params={{ quesiontionLibId: '0' }}
                search={{ source: 'chapterTable' }}
                className='text-sm text-[#0057B8] hover:underline'
              >
                View All
              </Link>
            </Flex>
            <div className='relative'>
              <IconSearch className='text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2' />
              <Input
                placeholder={'Search'}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className='h-10 w-[150px] pl-9 lg:w-[250px]'
              />
            </div>
          </Flex>
        </div>
        <Table>
          <TableHeader>
            <TableRow className='bg-[#F2F4F7]'>
              <TableHead className='font-mundial p-4 text-sm font-[600] text-[#002652]'>
                Chapter
              </TableHead>
              <TableHead className='font-mundial w-[140px] text-center text-sm font-[600] text-[#002652]'>
                Total Questions
              </TableHead>
              <TableHead className='font-mundial w-[140px] text-center text-sm font-[600] text-[#002652]'>
                Average AI Mark
              </TableHead>

              <TableHead className='font-mundial w-[140px] text-center text-sm font-[600] text-[#002652]'>
                Average Tutor Mark
              </TableHead>
              <TableHead className='font-mundial w-[20px] text-center text-sm font-[600] text-[#002652]'></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoadingClassChapter ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Flex justify={'center'}>
                    <Spinner size={'3'} />
                  </Flex>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {classChapters?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className='h-24 text-center'>
                      No data found.
                    </TableCell>
                  </TableRow>
                )}
                {classChapters?.map((item) => (
                  <TableRow key={item.id} className='hover:bg-gray-50'>
                    <TableCell className='w-[167px] p-4'>
                      <div>
                        <div className='truncate text-sm font-[400] text-[#002652]'>
                          {item.name}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className='w-[15%] text-center text-sm font-[300] text-[#002652]'>
                      {item.total_questions}
                    </TableCell>
                    <TableCell
                      className={cn(
                        'w-[15%] text-center text-sm font-[300] text-[#002652]',
                        getMarksStyle(item?.avg_ai_marks)
                      )}
                    >
                      {getMarksPercentage(item.avg_ai_marks)}
                    </TableCell>
                    <TableCell
                      className={cn(
                        'w-[15%] text-center text-sm font-[300] text-[#002652]',
                        getMarksStyle(item?.avg_tutor_marks)
                      )}
                    >
                      {getMarksPercentage(item.avg_tutor_marks)}
                    </TableCell>
                    <TableCell className='w-fit text-right'>
                      <Link
                        to='/question-lib/$quesiontionLibId'
                        params={{
                          quesiontionLibId: item.id.toString(),
                        }}
                        search={{ source: 'chapterTable' }}
                        className='rounded px-6 py-2 text-center text-[#0057B8] hover:bg-[#0057B8]/10'
                        onClick={() =>
                          setSelectedChapter({
                            chapter_id: item.id,
                            chapter_name: item.name,
                            total_questions: 0,
                          })
                        }
                      >
                        View detail
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </>
            )}
          </TableBody>
        </Table>

        {meta && meta.total_pages > 1 && (
          <div className='flex justify-end p-4'>
            <TablePaginate
              itemPerPage={meta.items_per_page}
              totalItems={meta.total_items}
              page={page}
              totalPages={meta.total_pages}
              onPageChange={handlePageChange}
              readOnlyPageDisplay={false}
              hideOnSinglePage={false}
            />
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default ChapterTable
